import { WifiOff, Loader2, RefreshCw, RotateCcw } from "lucide-react";
import type { NetworkQuality, SdkStatus } from "../hooks/useATClient";

interface NetworkBannerProps {
  networkQuality: NetworkQuality;
  sdkStatus: SdkStatus;
  onReconnect: () => void;
}

/**
 * Shown (after the SDK has connected at least once) when the connection is
 * degraded or offline.
 *
 *  degraded — SDK is re-initialising. Shows a spinner and context-aware message.
 *  poor     — SDK is offline or closed. Shows specific reason + Reconnect and
 *             Refresh page buttons so the user has two clear recovery paths.
 */
export function NetworkBanner({ networkQuality, sdkStatus, onReconnect }: NetworkBannerProps) {
  if (networkQuality === "good" || networkQuality === "unknown") return null;

  const isPoor = networkQuality === "poor";

  // ── Message copy — specific to the current SDK state ──────────────────────
  let title: string;
  let detail: string;

  if (!navigator.onLine) {
    title = "No internet connection";
    detail = "Check your Wi-Fi or mobile data, then reconnect.";
  } else if (isPoor) {
    if (sdkStatus === "closed") {
      title = "Connection dropped";
      detail = "The calling service disconnected. Try reconnecting or refresh the page.";
    } else if (sdkStatus === "notready") {
      title = "Calling service unavailable";
      detail = "The dialer is connected but not ready to make calls. Try reconnecting.";
    } else {
      // offline or any other poor state
      title = "Calls unavailable";
      detail = "Lost connection to the calling service. Try reconnecting or refresh the page.";
    }
  } else {
    // degraded — currently reconnecting
    if (sdkStatus === "initialising") {
      title = "Reconnecting…";
      detail = "Restoring your connection to the calling service.";
    } else {
      title = "Connection unstable";
      detail = "Your connection is degraded. Call quality may be affected.";
    }
  }

  return (
    <div
      className={`border-b px-4 py-3 ${
        isPoor ? "border-destructive/20 bg-destructive/10" : "border-amber-200 bg-amber-50"
      }`}
    >
      {/* Top row: icon + title + spinner/action */}
      <div className="flex items-center gap-2.5">
        <WifiOff className={`h-4 w-4 shrink-0 ${isPoor ? "text-destructive" : "text-amber-600"}`} />
        <p
          className={`flex-1 text-xs font-semibold ${
            isPoor ? "text-destructive" : "text-amber-700"
          }`}
        >
          {title}
        </p>
        {!isPoor && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-600" />}
      </div>

      {/* Detail text */}
      <p
        className={`mt-1 pl-[26px] text-xs leading-snug ${
          isPoor ? "text-destructive/80" : "text-amber-600"
        }`}
      >
        {detail}
      </p>

      {/* Action buttons — only shown when in a poor/failed state */}
      {isPoor && (
        <div className="mt-2.5 flex gap-2 pl-[26px]">
          <button
            onClick={onReconnect}
            className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/20"
          >
            <RefreshCw className="h-3 w-3" />
            Reconnect
          </button>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-1.5 rounded-md border border-destructive/20 px-3 py-1.5 text-xs font-medium text-destructive/70 transition-colors hover:bg-destructive/5"
          >
            <RotateCcw className="h-3 w-3" />
            Refresh page
          </button>
        </div>
      )}
    </div>
  );
}
